import React, { useEffect, useState } from 'react';
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../useAuth';
import './settings.css';

const ORDER_SMS_SETTINGS_REF = doc(db, 'AppSettings', 'orderSms');
const FIRESTORE_BATCH_LIMIT = 500;

function Settings() {
  const { user, claims, loading: authLoading } = useAuth();
  const [smsFullProgress, setSmsFullProgress] = useState(true);
  const [smsIncludeAccountLink, setSmsIncludeAccountLink] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSmsSetting, setSavingSmsSetting] = useState(false);
  const [smsSaveError, setSmsSaveError] = useState('');
  const [bulkOptInLoading, setBulkOptInLoading] = useState(false);
  const [bulkOptInMessage, setBulkOptInMessage] = useState('');
  const [bulkOptInError, setBulkOptInError] = useState('');

  const isAdmin = claims.admin === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      setSettingsLoaded(true);
      return;
    }

    const unsub = onSnapshot(
      ORDER_SMS_SETTINGS_REF,
      (snap) => {
        if (!snap.exists()) {
          setSmsFullProgress(true);
          setSmsIncludeAccountLink(false);
        } else {
          const data = snap.data() || {};
          const v = data.smsFullProgressNotifications;
          setSmsFullProgress(typeof v === 'boolean' ? v : true);
          setSmsIncludeAccountLink(
            typeof data.smsIncludeAccountLink === 'boolean' ? data.smsIncludeAccountLink : false
          );
        }
        setSettingsLoaded(true);
        setSmsSaveError('');
      },
      (err) => {
        console.error('Settings snapshot error:', err);
        setSettingsLoaded(true);
        setSmsSaveError(
          'Could not load SMS settings. If this persists, deploy the latest Firestore rules (AppSettings) or sign out and back in.'
        );
      }
    );
    return () => unsub();
  }, [authLoading, user, isAdmin]);

  const saveOrderSmsSetting = async (fields) => {
    if (!user || !isAdmin) return;
    setSavingSmsSetting(true);
    setSmsSaveError('');
    try {
      await user.getIdToken(true);
      await setDoc(
        ORDER_SMS_SETTINGS_REF,
        {
          ...fields,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.error('Failed to save SMS setting:', e);
      setSmsSaveError(
        'Could not save. Deploy Firestore rules if you have not yet, or sign out and back in so your admin token refreshes.'
      );
    } finally {
      setSavingSmsSetting(false);
    }
  };

  const handleSmsFullToggle = (next) => saveOrderSmsSetting({ smsFullProgressNotifications: next });

  const handleSmsAccountLinkToggle = (next) => saveOrderSmsSetting({ smsIncludeAccountLink: next });

  const handleBulkSmsOptInAllCustomers = async () => {
    if (!user || !isAdmin) return;
    setBulkOptInMessage('');
    setBulkOptInError('');
    let customerSnap;
    let optOutSnap;
    try {
      [customerSnap, optOutSnap] = await Promise.all([
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'SMSOptOuts')),
      ]);
    } catch (e) {
      console.error('Failed to read Customers / SMSOptOuts:', e);
      setBulkOptInError('Could not read customers or opt-outs. Check your connection and permissions.');
      return;
    }

    const optedOutPhones = new Set();
    optOutSnap.docs.forEach((d) => {
      const data = d.data() || {};
      if (data.optedOut !== true) return;
      const fromId = String(d.id || '').replace(/\D/g, '').slice(-10);
      const fromField = String(data.phoneNumber || data.phone || '')
        .replace(/\D/g, '')
        .slice(-10);
      if (fromId.length === 10) optedOutPhones.add(fromId);
      if (fromField.length === 10) optedOutPhones.add(fromField);
    });

    const docsToUpdate = customerSnap.docs.filter((d) => {
      const data = d.data() || {};
      const phone10 = String(data.phone || d.id || '')
        .replace(/\D/g, '')
        .slice(-10);
      if (phone10.length === 10 && optedOutPhones.has(phone10)) return false;
      return true;
    });

    const skipped = customerSnap.size - docsToUpdate.length;
    if (docsToUpdate.length === 0) {
      setBulkOptInMessage(
        skipped > 0
          ? `No customers updated. All ${skipped} profile(s) are on the SMS opt-out list.`
          : 'There are no customer documents to update.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Set SMS subscribed (opt-in) to ON for ${docsToUpdate.length} customer profile(s)?\n\n` +
        `${skipped} customer(s) on the STOP / SMS opt-out list will be skipped and left unchanged.\n\n` +
        'Continue?'
    );
    if (!confirmed) return;

    setBulkOptInLoading(true);
    try {
      await user.getIdToken(true);
      let updated = 0;
      for (let i = 0; i < docsToUpdate.length; i += FIRESTORE_BATCH_LIMIT) {
        const slice = docsToUpdate.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = writeBatch(db);
        slice.forEach((d) => {
          batch.set(
            doc(db, 'Customers', d.id),
            { smsSubscribed: true, updatedAt: serverTimestamp() },
            { merge: true }
          );
        });
        await batch.commit();
        updated += slice.length;
      }
      setBulkOptInMessage(
        `Updated ${updated} customer profile(s): SMS subscribed is now on.` +
          (skipped > 0 ? ` Skipped ${skipped} opted-out customer(s).` : '')
      );
    } catch (e) {
      console.error('Bulk SMS opt-in failed:', e);
      setBulkOptInError(
        e?.message ||
          'Update failed. Deploy Firestore rules if needed, or sign out and back in to refresh your admin token.'
      );
    } finally {
      setBulkOptInLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
        </div>
        <p className="settings-status">Loading…</p>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
        </div>
        <p className="settings-error">You need admin access to view this page.</p>
      </div>
    );
  }

  if (!settingsLoaded) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
        </div>
        <p className="settings-status">Loading…</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-card settings-card-sms">
        <h2 className="settings-section-title">Customer SMS (opted-in only)</h2>
        <p className="settings-section-desc">
          Controls automatic texts when an order changes. Customers only receive messages if they are opted in on their
          customer profile.
        </p>

        <div className="settings-toggle-row">
          <div className="settings-toggle-copy">
            <span className="settings-toggle-label">Full order SMS updates</span>
            <span className="settings-toggle-hint">
              <strong>On:</strong> notify when the order is marked for alteration, when a tailor is assigned, and when
              the order is ready for pick up (current behavior).
              <br />
              <strong>Off:</strong> only send when the order is ready for pick up.
            </span>
          </div>
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={smsFullProgress}
              disabled={!settingsLoaded || savingSmsSetting}
              onChange={(e) => handleSmsFullToggle(e.target.checked)}
              aria-label="Full order SMS updates"
            />
            <span className="settings-switch-slider" />
          </label>
        </div>

        <div className="settings-toggle-row">
          <div className="settings-toggle-copy">
            <span className="settings-toggle-label">Send rewards link when an order is created</span>
            <span className="settings-toggle-hint">
              <strong>Off:</strong> status texts only (alteration, assigned, and ready for pick up stay the same).
              <br />
              <strong>On:</strong> when Shopify marks the payment as paid, send a thank-you text with their new rewards point total and a link to the rewards page (jandzcouture.com/pages/rewards-program) with their phone filled in. They still tap Send code themselves. Unpaid orders do not earn points.
            </span>
          </div>
          <label className="settings-switch">
            <input
              type="checkbox"
              checked={smsIncludeAccountLink}
              disabled={!settingsLoaded || savingSmsSetting}
              onChange={(e) => handleSmsAccountLinkToggle(e.target.checked)}
              aria-label="Send rewards link when an order is created"
            />
            <span className="settings-switch-slider" />
          </label>
        </div>

        {savingSmsSetting && <p className="settings-status">Saving…</p>}
        {smsSaveError ? <p className="settings-error">{smsSaveError}</p> : null}
      </div>

      <div className="settings-card settings-card-bulk">
        <h2 className="settings-section-title">Bulk customer SMS opt-in</h2>
        <p className="settings-section-desc">
          Sets <code>smsSubscribed: true</code> on customers who are not on the STOP / <strong>SMSOptOuts</strong> list.
          Opted-out profiles are skipped and left unchanged.
        </p>
        <button
          type="button"
          className="settings-bulk-opt-in-btn"
          onClick={handleBulkSmsOptInAllCustomers}
          disabled={bulkOptInLoading || savingSmsSetting}
        >
          {bulkOptInLoading ? 'Updating customers…' : 'Opt in all customers (SMS)'}
        </button>
        {bulkOptInLoading && <p className="settings-status">Writing batches… do not close this tab.</p>}
        {bulkOptInMessage ? <p className="settings-success">{bulkOptInMessage}</p> : null}
        {bulkOptInError ? <p className="settings-error">{bulkOptInError}</p> : null}
      </div>
    </div>
  );
}

export default Settings;
